import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '../../api/client'
import type { AiEmbeddingModel, AiEmbeddingStatus } from '../../api/schema'
import { notify } from '../../lib/notifications'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'

const BASE = '/api/v1/organizations/{orgCode}/settings/ai-embedding'

/**
 * Embedding models and the switch between them (spec 2026-10-10-02). Everyone
 * reads it; platform admins pick the model to move to, watch its backfill, and
 * activate it once every document has a vector. Search keeps the current model
 * until then, so nothing is ever lost at the flip.
 */
export function EmbeddingSwitchSection({ org }: { org: string }) {
    const { t } = useTranslation('settings')
    const queryClient = useQueryClient()
    const queryKey = ['ai-embedding', org]

    const { data } = useQuery({
        queryKey,
        queryFn: async (): Promise<AiEmbeddingStatus> => {
            const { data: body, error } = await apiClient.GET(BASE, { params: { path: { orgCode: org } } })
            if (error || !body) throw new Error('Failed to load the embedding models')
            return body.data
        },
        // The backfill runs in the background: follow it while a switch is under way.
        refetchInterval: (query) => (query.state.data?.pending ? 4000 : false),
    })

    const refresh = (status: AiEmbeddingStatus) => queryClient.setQueryData(queryKey, status)
    const onError = (error: unknown) => {
        const code = (error as { code?: string } | undefined)?.code
        notify({
            key: 'ai_embedding_error',
            variant: 'destructive',
            titleKey: 'settings:ai_models.embedding.error_title',
            descriptionKey:
                code === 'embedding_backfill_incomplete'
                    ? 'settings:ai_models.embedding.error_incomplete'
                    : 'settings:ai_models.embedding.error_description',
            timeoutMs: 0,
        })
    }
    const run = async (call: Promise<{ data?: { data: AiEmbeddingStatus }; error?: unknown }>) => {
        const { data: body, error } = await call
        if (error || !body) throw error ?? new Error('request failed')
        refresh(body.data)
        return body.data
    }
    const params = { params: { path: { orgCode: org } } }

    const select = useMutation({
        mutationFn: (code: string) => run(apiClient.POST(`${BASE}/pending`, { ...params, body: { code } })),
        onError,
    })
    const cancel = useMutation({ mutationFn: () => run(apiClient.DELETE(`${BASE}/pending`, params)), onError })
    const backfill = useMutation({ mutationFn: () => run(apiClient.POST(`${BASE}/backfill`, params)), onError })
    const activate = useMutation({
        mutationFn: () => run(apiClient.POST(`${BASE}/activate`, params)),
        onSuccess: () =>
            notify({ key: 'ai_embedding_activated', variant: 'default', titleKey: 'settings:ai_models.embedding.activated' }),
        onError,
    })
    const drop = useMutation({
        mutationFn: (code: string) =>
            run(
                apiClient.DELETE(`${BASE}/models/{code}`, {
                    params: { path: { orgCode: org, code } },
                })
            ),
        onError,
    })

    if (!data) {
        return (
            <section className="bg-card rounded-lg border border-border p-6 text-muted-foreground" data-testid="ai-embedding-loading">
                {t('ai_models.loading')}
            </section>
        )
    }

    const busy = select.isPending || cancel.isPending || backfill.isPending || activate.isPending || drop.isPending
    const canWrite = data.can_write
    const pendingModel = data.models.find((m) => m.pending)

    const coverage = (m: AiEmbeddingModel) => (
        <span data-testid={`ai-embedding-coverage-${m.code}`}>
            {t('ai_models.embedding.coverage', { embedded: m.embedded, total: data.eligible_documents })}
        </span>
    )

    return (
        <section className="bg-card rounded-lg border border-border p-6 space-y-6" data-testid="ai-embedding-section">
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('ai_models.embedding.title')}</h2>
                <p className="text-muted-foreground text-sm mt-1">{t('ai_models.embedding.description')}</p>
            </div>

            <ul className="space-y-4">
                {data.models.map((m) => (
                    <li key={m.code} className="rounded-md border border-border p-4 space-y-3" data-testid={`ai-embedding-model-${m.code}`}>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{m.name}</span>
                            <span className="text-xs text-muted-foreground">
                                {m.model} · {m.dimensions}
                                {m.provider ? ` · ${m.provider}` : ''}
                            </span>
                            {m.active && (
                                <Badge variant="success" data-testid={`ai-embedding-status-${m.code}`}>
                                    {t('ai_models.embedding.status.active')}
                                </Badge>
                            )}
                            {m.pending && (
                                <Badge variant="info" data-testid={`ai-embedding-status-${m.code}`}>
                                    {t('ai_models.embedding.status.pending')}
                                </Badge>
                            )}
                            {!m.active && !m.pending && !m.available && (
                                <Badge variant="warning" data-testid={`ai-embedding-status-${m.code}`}>
                                    {t('ai_models.embedding.status.unavailable')}
                                </Badge>
                            )}
                            {!m.active && !m.pending && m.available && m.has_partition && (
                                <Badge variant="secondary" data-testid={`ai-embedding-status-${m.code}`}>
                                    {t('ai_models.embedding.status.kept')}
                                </Badge>
                            )}
                        </div>

                        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                            {coverage(m)}
                            {!m.active && m.missing > 0 && (
                                <span data-testid={`ai-embedding-cost-${m.code}`}>
                                    {t('ai_models.embedding.to_embed', { count: m.missing })}
                                    {' · '}
                                    {t('ai_models.embedding.estimated_cost', { cost: m.estimated_cost_usd.toFixed(2) })}
                                </span>
                            )}
                        </div>

                        {m.pending && (
                            <div className="space-y-1" data-testid="ai-embedding-progress">
                                <div className="h-2 w-full rounded bg-muted overflow-hidden">
                                    <div
                                        className="h-full bg-primary transition-all"
                                        style={{
                                            width: `${data.eligible_documents ? Math.round((m.embedded / data.eligible_documents) * 100) : 100}%`,
                                        }}
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {t('ai_models.embedding.jobs_in_flight', { count: data.jobs_in_flight })}
                                </p>
                            </div>
                        )}

                        {canWrite && (
                            <div className="flex flex-wrap gap-2">
                                {!m.active && !m.pending && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!m.available || busy || Boolean(pendingModel)}
                                        onClick={() => select.mutate(m.code)}
                                        data-testid={`ai-embedding-switch-${m.code}`}
                                    >
                                        {t('ai_models.embedding.switch_to')}
                                    </Button>
                                )}
                                {m.pending && (
                                    <>
                                        <Button
                                            size="sm"
                                            disabled={!data.can_activate || busy}
                                            onClick={() => activate.mutate()}
                                            data-testid="ai-embedding-activate"
                                        >
                                            {t('ai_models.embedding.activate')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={busy}
                                            onClick={() => backfill.mutate()}
                                            data-testid="ai-embedding-backfill"
                                        >
                                            {t('ai_models.embedding.relaunch')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            disabled={busy}
                                            onClick={() => cancel.mutate()}
                                            data-testid="ai-embedding-cancel"
                                        >
                                            {t('ai_models.embedding.cancel')}
                                        </Button>
                                    </>
                                )}
                                {!m.active && !m.pending && m.has_partition && (
                                    <DeleteConfirmDialog
                                        trigger={
                                            <Button size="sm" variant="ghost" disabled={busy} data-testid={`ai-embedding-drop-${m.code}`}>
                                                {t('ai_models.embedding.drop')}
                                            </Button>
                                        }
                                        title={t('ai_models.embedding.drop_title', { model: m.name })}
                                        description={t('ai_models.embedding.drop_description')}
                                        onConfirm={() => drop.mutate(m.code)}
                                        isDeleting={drop.isPending}
                                    />
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            <p className="text-xs text-muted-foreground" data-testid="ai-embedding-note">
                {t('ai_models.embedding.note')}
            </p>
            {!canWrite && (
                <p className="text-xs text-muted-foreground" data-testid="ai-embedding-read-only">
                    {t('ai_models.read_only')}
                </p>
            )}
        </section>
    )
}
